import { billing, unwrap, ApiError } from "../api";

/** Ported from apps/admin/app/(app)/invoicing/[id]/registration/{page,actions}.tsx, through the generated billing client. */

export type RegistrationStatus = {
  irnStatus: string;
  irn: string | null;
  irnError: string | null;
  nextAttemptDueAt: string | null;
  attempts: readonly {
    attemptNo: number | string;
    startedAt: string;
    outcome: string | null;
    error: string | null;
    resolvedByStatusQuery: boolean;
  }[];
};

export async function getRegistrationStatus(invoiceId: string): Promise<RegistrationStatus> {
  return unwrap(
    (await billing()).GET("/api/billing/registration/status", { params: { query: { invoiceId } } }),
  ) as Promise<RegistrationStatus>;
}

export async function registerInvoice(id: string): Promise<{ outcome?: string }> {
  return (
    ((await unwrap(
      (await billing()).POST("/api/billing/registration/invoices/{id}", { params: { path: { id } } }),
    )) as { outcome?: string }) ?? {}
  );
}

export type RetryCounts = { awaiting?: number; retried?: number; registered?: number; waiting?: number; exhausted?: number };

export async function retryAwaitingRegistrations(): Promise<RetryCounts> {
  return (
    ((await unwrap((await billing()).POST("/api/billing/registration/retry"))) as RetryCounts) ?? {}
  );
}

/**
 * The problem document Billing returns when the portal's cancellation window has closed —
 * matched on `code`, not status, per apps/admin's original `windowClosedAt` helper.
 */
export function windowClosedAt(error: unknown): string | null {
  if (!(error instanceof ApiError) || !error.raw) return null;
  if (error.raw.code !== "irn_cancellation_window_passed") return null;
  return typeof error.raw.windowClosedAt === "string" ? error.raw.windowClosedAt : "";
}

/** Throws the ApiError on refusal; caller inspects `windowClosedAt(err)` for the fallback path. */
export async function cancelIrn(id: string, reason: string): Promise<void> {
  await unwrap(
    (await billing()).POST("/api/billing/registration/invoices/{id}/cancel", {
      params: { path: { id } },
      body: { reason },
    }),
  );
}
